// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 40,
  right: 100,
  bottom: 25,
  left: 35,
};

let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let etheopianAirDate;
let lionAirDate;
let stockTumble;
let alaskaAnnoDate;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

d3.json('https://ix.cnn.io/data/boeing-share-price/boeing_share_price.json').then((data) => {
  rawData = data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    close: +d.close,
  }));

  chartData = rawData.filter(d => d.date > pTime('2014-12-31'));

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  maxValue = d3.max(chartData, d => d.close);

  lastData = chartData.filter(d => d.date == maxDomain)[0];

  lionAirDate = chartData.filter(d => d.date > pTime('2018-10-28') && d.date < pTime('2018-10-30'))[0];
  etheopianAirDate = chartData.filter(d => d.date > pTime('2019-03-08') && d.date < pTime('2019-03-12'))[0];
  stockTumble = chartData.filter(d => d.date > pTime('2020-03-01') && d.date < pTime('2020-03-03'))[0];
  alaskaAnnoDate = chartData.filter(d => d.date > pTime('2024-01-04') && d.date < pTime('2024-01-08'))[0];


  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%Y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickFormat(d3.format('$.1s'))
    .tickSize(-chartWidth);

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.close));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const line = g.append('g')
    .attr('class', 'line');

  line.append('path')
    .datum(chartData)
    .attr('stroke', d => '#7c4ea5')
    .attr('stroke-width', '1')
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();


  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.close)]})`);

  annotation.append('circle')
    .attr('r', '4')
    .attr('fill', '#7c4ea5')
    .attr('stroke', 'none');

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text(d3.timeFormat('%b %-d')(lastData.date));

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text(d3.format('$')(lastData.close))
    .attr('dy', '1.2em');

  const etheopianAirAnno = g.append('g')
    .attr('class', 'annotation-etheopian')
    .attr('transform', `translate(${[xScale(etheopianAirDate.date), yScale(etheopianAirDate.close)]})`);

  etheopianAirAnno.append('line')
    .attr('x1', xScale(d3.timeFormat('%b %-d')(etheopianAirDate.date)))
    .attr('y1', 95)
    .attr('x2', xScale(d3.timeFormat('%b %-d')(etheopianAirDate.date)))
    .attr('y2', 35)
    .style('stroke-width', 1)
    .style('stroke', '#999')
    .style('fill', 'none');

  etheopianAirAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text('Mar. 10,')
    .attr('dy', '8em')
    .attr('dx', '-2.5em');

  etheopianAirAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text('2019')
    .attr('dy', '9.2em')
    .attr('dx', '-2.5em');

  etheopianAirAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text('Ethiopian')
    .attr('dy', '10.4em')
    .attr('dx', '-2.5em');

  etheopianAirAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text('Airlines')
    .attr('dy', '11.6em')
    .attr('dx', '-2.5em');

  etheopianAirAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text('crash')
    .attr('dy', '12.7em')
    .attr('dx', '-2.5em');

  const lionAirAnno = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lionAirDate.date), yScale(lionAirDate.close)]})`);

  lionAirAnno.append('line')
    .attr('x1', xScale(d3.timeFormat('%b %-d')(lionAirDate.date)))
    .attr('y1', -102)
    .attr('x2', xScale(d3.timeFormat('%b %-d')(lionAirDate.date)))
    .attr('y2', -40)
    .style('stroke-width', 1)
    .style('stroke', '#999')
    .style('fill', 'none');

  lionAirAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text('Oct. 29, 2018')
    .attr('dy', '-6.5em')
    .attr('dx', '-6.9em');

  lionAirAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text('Lion Air crash')
    .attr('dy', '-5.2em')
    .attr('dx', '-6.7em');

  const covidAnno = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(stockTumble.date), yScale(stockTumble.close)]})`);

  covidAnno.append('line')
    .attr('x1', xScale(d3.timeFormat('%b %-d')(stockTumble.date)))
    .attr('y1', -122)
    .attr('x2', xScale(d3.timeFormat('%b %-d')(stockTumble.date)))
    .attr('y2', -50)
    .style('stroke-width', 1)
    .style('stroke', '#999')
    .style('fill', 'none');

  covidAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text('March 2020')
    .attr('dy', '-8em')
    .attr('dx', '.4em');

  covidAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text('Covid-19')
    .attr('dy', '-6.7em')
    .attr('dx', '.4em');

  const alaskaAnno = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(alaskaAnnoDate.date), yScale(alaskaAnnoDate.close)]})`);

  alaskaAnno.append('line')
    .attr('x1', xScale(d3.timeFormat('%b %-d')(alaskaAnnoDate.date)))
    .attr('y1', -70)
    .attr('x2', xScale(d3.timeFormat('%b %-d')(alaskaAnnoDate.date)))
    .attr('y2', -20)
    .style('stroke-width', 1)
    .style('stroke', '#999')
    .style('fill', 'none');

  alaskaAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text('Jan. 5')
    .attr('dy', '-4.3em')
    .attr('dx', '.4em');

  alaskaAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text('Alaska Airlines')
    .attr('dy', '-3em')
    .attr('dx', '.4em');

  alaskaAnno.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text('door incident')
    .attr('dy', '-1.7em')
    .attr('dx', '.4em');

  window.pymChild.sendHeight();
}
